import { useState } from "react";
import { Camera, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { SignaturePad } from "@/components/SignaturePad";
import { api, formatErr, getPosition, uploadBlob, dataUrlToBlob, MGPS_CATS } from "@/lib/api";

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl border p-5">
    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">{title}</div>
    {children}
  </div>
);

export const TaskCompleteForm = ({ task, onDone }) => {
  const mgps = MGPS_CATS.includes(task.category);
  const needsProof = task.category !== "sales_visit";
  const [checklist, setChecklist] = useState(task.checklist || []);
  const [psi, setPsi] = useState("");
  const [leak, setLeak] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [sig, setSig] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (i) => setChecklist((c) => c.map((x, j) => (j === i ? { ...x, done: !x.done } : x)));
  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (f) setPhoto({ file: f, url: URL.createObjectURL(f) });
  };

  const ready = (!needsProof || (photo && sig)) && (!mgps || (psi !== "" && leak !== null));

  const submit = async () => {
    setBusy(true);
    try {
      const pos = await getPosition();
      const proof_photo_path = photo ? await uploadBlob(photo.file, photo.file.name || "proof.jpg") : null;
      const customer_signature_path = sig ? await uploadBlob(await dataUrlToBlob(sig), "signature.png") : null;
      const { data } = await api.post(`/tasks/${task.id}/complete`, {
        ...pos, proof_photo_path, customer_signature_path, completion_notes: notes, checklist,
        mgps_oxygen_pressure_psi: mgps ? Number(psi) : null, mgps_leakage_test_passed: mgps ? leak : null,
      });
      toast.success("Task marked as completed");
      onDone(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Section title="Checklist">
        <div className="space-y-2.5">
          {checklist.map((c, i) => (
            <label key={c.item} className="flex items-center gap-3 text-sm text-slate-800 cursor-pointer">
              <Checkbox checked={c.done} onCheckedChange={() => toggle(i)} data-testid={`task-checklist-item-${i}`} />{c.item}
            </label>
          ))}
        </div>
      </Section>
      {mgps && (
        <Section title="MGPS Readings">
          <Label className="text-xs">Oxygen line pressure (PSI)</Label>
          <Input type="number" inputMode="decimal" min="0" max="999" step="0.1" value={psi} onChange={(e) => setPsi(e.target.value)}
            className="h-12 text-2xl font-mono-num" placeholder="e.g. 55" data-testid="task-psi-input" />
          <div className="flex items-center justify-between mt-4 rounded-lg border p-3">
            <div>
              <div className="text-sm font-semibold">Leakage test passed?</div>
              <div className="text-xs text-slate-500">{leak === null ? "Not recorded" : leak ? "Passed" : "Failed"}</div>
            </div>
            <Switch checked={!!leak} onCheckedChange={(v) => setLeak(v)} onClick={() => leak === null && setLeak(true)} data-testid="task-leakage-test-toggle" />
          </div>
          {leak === null && <button type="button" onClick={() => setLeak(false)} className="text-xs text-rose-600 mt-2 font-semibold" data-testid="task-leakage-fail-button">Record as failed</button>}
        </Section>
      )}
      <Section title={`Site Photo${needsProof ? " (required)" : ""}`}>
        <label className="block cursor-pointer">
          {photo ? <img src={photo.url} alt="proof" className="w-full h-52 object-cover rounded-xl" data-testid="task-photo-preview" />
            : <div className="h-32 rounded-xl border-2 border-dashed grid place-items-center text-slate-500 text-sm"><div className="text-center"><Camera className="h-6 w-6 mx-auto mb-1" />Take photo of gauge / equipment</div></div>}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="task-photo-input" />
        </label>
      </Section>
      <Section title={`Hospital Sign-off${needsProof ? " (required)" : ""}`}>
        <SignaturePad onChange={setSig} />
      </Section>
      <Section title="Notes">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observations, parts replaced…" data-testid="task-notes-input" />
      </Section>
      <Button onClick={submit} disabled={!ready || busy} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base" data-testid="task-complete-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-2" />Mark as Completed</>}
      </Button>
    </div>
  );
};
